import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import '../../styles/common.css';
import '../../styles/projects.css';
import '../../styles/modal.css';
import '../../styles/jira-items.css';
import axios from 'axios';
import { dateFormat } from '../../utils/dateFormat';
import { Pagination } from '../ui';
import DropdownMenu from './DropdownMenu';
// import { ToastContainer, toast } from 'react-toastify'; // Replaced with actionFeedback
import { useRef } from 'react';
import { useLocation } from 'react-router';
import useProjectData from '../../hooks/useProjectData';
import { Navbar } from '../navigation';
import axiosInstance from '../../api/axiosInstance';
import { useDebounce } from '../../hooks/useDebounce';
import { toast } from 'react-toast';
import { CreateProjectModal, DeleteModal, DuplicateModal, EditModal, JiraImportModal, JiraSyncModal, LinkProjectModal, LinkRepositoryModal, ViewModal } from './Modals';
import { ProjectStatusBadge, ProjectTypeBadge } from '../common';
import { useProject } from '../../context/ProjectContext';
import useWorkspaceRouting from '../../hooks/useWorkspaceRouting';
import { buildWorkspaceDashboardPath } from '../../utils/workspaceRouteUtils';
const API_BASE_URL = process.env.REACT_APP_BASE_URL;

const getProjectStatus = (project) =>
  String(project?.status ?? project?.originalData?.status ?? '')
    .trim()
    .toLowerCase();

/** Only active projects can be edited. Inactive, archived, and deleted are locked. */
const canEditProject = (project) => getProjectStatus(project) === 'active';

/** Only active projects can be deleted. Inactive and archived must be restored first. */
const canDeleteProject = (project) => getProjectStatus(project) === 'active';

const ProjectsList = ({ onCreateProject, embedded = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectProject } = useProject();
  const { getWorkspaceIdFromURL } = useWorkspaceRouting();
  
  // Define showActionFeedback early so it can be passed to useProjectData
  const [actionFeedback, setActionFeedback] = useState(null);
  
  const showActionFeedback = (message, type = 'success') => {
    setActionFeedback({ message, type });
    setTimeout(() => {
      setActionFeedback(null);
    }, 3000);
  };

  const {
    projects,
    filteredProjects,
    isLoading,
    error,
    currentPage,
    totalPages,
    pageSize,
    activeTab,
    statusCounts,
    setCurrentPage,
    setActiveTab,
    setFilteredProjects,
    updateProjectStatus,
    deleteProject,
    duplicateProject,
    updateProject,
    fetchProjects,
    searchTerm,
    setSearchTerm,
    isBitbucketConfigured,
  } = useProjectData();


  const [isJiraModalOpen, setIsJiraModalOpen] = useState(false);
  const [isJiraSyncModalOpen, setIsJiraSyncModalOpen] = useState(false);
  const [isLinkRepositoryModalOpen, setIsLinkRepositoryModalOpen] = useState(false);
  const [isLinkProjectModalOpen, setIsLinkProjectModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectForJiraSync, setProjectForJiraSync] = useState(null);
  const [projectForLinkRepository, setProjectForLinkRepository] = useState(null);
  const [projectForLinkProject, setProjectForLinkProject] = useState(null);
  const [repositoryLinkEditMode, setRepositoryLinkEditMode] = useState(false);
  const [repositoryLinkInitialData, setRepositoryLinkInitialData] = useState(null);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [projectToDuplicate, setProjectToDuplicate] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editProjectType, setEditProjectType] = useState('');
  const [editProjectStatus, setEditProjectStatus] = useState('');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  // actionFeedback state moved up to be available for useProjectData hook
  const [createProjectModalOpen, setCreateProjectModalOpen] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [projectType, setProjectType] = useState('');
  const [projectStatus, setProjectStatus] = useState('active');
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const dropdownTriggerRef = useRef(null);
  const tableContainerRef = useRef(null);

  // Debounced search for immediate UI feedback
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const handleCreateProject = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(
        '/projects/add_project',
        {
          project_name: projectName,
          description: projectDescription,
          project_type: projectType,
          status: projectStatus,
          workspace_id: location.pathname.includes('/dashboard') ? parseInt(location.pathname.split('/')[1].split('_')[1], 10) : null
        }
      );

      if (response.status_code == 201) {
        // showActionFeedback('Project created successfully', 'success');
        setCreateProjectModalOpen(false);
        // Reset form
        setProjectName('');
        setProjectDescription('');
        setProjectType('');
        setProjectStatus('active');
        // Fetch updated project list
        fetchProjects(currentPage, activeTab, searchTerm, sortConfig);
        // if (onCreateProject) {
        //   onCreateProject(response.data);
        // }
      }
    } catch (error) {
      console.error('Error creating project:', error);
      showActionFeedback(error.response?.data?.message || 'Failed to create project', 'error');
    } finally {
      setLoading(false);
    }
  };

  const toggleCreateProjectModal = () => {
    setCreateProjectModalOpen(!createProjectModalOpen);
  };
  
  const handleMoreClick = (e, projectId) => {
    e.stopPropagation();
    dropdownTriggerRef.current = e.currentTarget;
    setActiveDropdown(activeDropdown === projectId ? null : projectId);
  };

  useEffect(() => {
    fetchProjects(currentPage, activeTab, debouncedSearchTerm, sortConfig);
  }, [debouncedSearchTerm, currentPage, activeTab, sortConfig, fetchProjects]);

  useEffect(() => {
    if (location.state?.searchTerm) {
      setSearchTerm(location.state.searchTerm);
    }
  }, [location.state]);

const handleUnarchiveProject = async (id) => {
  const success = await updateProjectStatus(id, 'active');
  if (success) {
   
    setActiveDropdown(null);
  }
};

  const handleActivateProject = async (id) => {
    const project = projects.find((p) => p.id === id);
    if (!project) return;

    const success = await updateProject(id, {
      name: project.name,
      description: project.description,
      type: project.type,
      status: 'active',
    });
    if (success) {
      await fetchProjects(currentPage, activeTab);
      setActiveDropdown(null);
    }
  };

  function getDropdownLabel(project) {
    switch (project?.originalData?.integration_source) {
      case 1:
        return 'JIRA Items';
      case 2:
        return 'WorkXiq Items';
      case 3:
        return 'View Test cases'  
      case null:
        return 'Link Project';
      default:
        return null;
    }
  }

  function handleDropdownClick(project){
      const integrationSource = project?.originalData?.integration_source;
        if (project?.originalData?.integration_source === null) {
          handleOpenLinkProjectModal(project);
        } else {
          handleViewJiraItems(project.id , integrationSource);
        }
  }

 const TesPlanIcon= (project)=>{
    return !project?.originalData?.test_plan ? (
      <>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
               <span
              style={{
                color: "red",
                fontWeight: "bold",
                cursor: "help",
              }}
              title="Coming Soon"
            >
              *
            </span>
      </>
        ) : (
          <>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M21 12a9 9 0 1 1-3-7" />
            <polyline points="21 3 21 9 15 9" />
          </svg>
               <span
              style={{
                color: "red",
                fontWeight: "bold",
                cursor: "help",
              }}
              title="Coming Soon"
            >
              *
            </span>
          </>
        )
 }

  const getDropdownItems = (project) => {
    if (project?.status?.toLowerCase() === 'archived') {
      return [
        {
          label: 'Unarchive',
          icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="1" y="3" width="22" height="5"></rect>
              <line x1="10" y1="12" x2="14" y2="12"></line>
            </svg>
          ),
          onClick: () => handleUnarchiveProject(project.id)
        }
      ];
    }

    if (project?.status?.toLowerCase() === 'inactive') {
      return [
        {
          label: 'Activate Project',
          icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
          ),
          onClick: () => handleActivateProject(project.id),
        },
      ];
    }

    return [
      {
        label: getDropdownLabel(project),
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 3l1.912 5.813a2 2 0 0 0 1.899 1.37h6.19l-5.011 3.636a2 2 0 0 0-.726 2.236l1.91 5.812-5.01-3.636a2 2 0 0 0-2.35 0l-5.01 3.636 1.91-5.812a2 2 0 0 0-.726-2.236l-5.01-3.636h6.19a2 2 0 0 0 1.899-1.37l1.912-5.813z"></path>
          </svg>
        ),
        onClick: () => handleDropdownClick(project),
        hidden:
          project?.originalData?.integration_source === null ? false : false,
      },
      {
        label: project?.originalData?.is_repo_linked
          ? "Update Repository"
          : "Link Repository",
        icon: (
          <>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 19H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h4M15 19h4a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-4"></path>
              <line x1="9" y1="11" x2="15" y2="11"></line>
            </svg>
            {!isBitbucketConfigured && (
              <span
                style={{
                  color: "red",
                  fontWeight: "bold",
                  cursor: "help",
                }}
                title="Please connect Bitbucket first to enable this functionality."
              >
                *
              </span>
            )}
          </>
        ),
        onClick: () =>
          project?.originalData?.is_repo_linked
            ? handleOpenUpdateRepositoryModal(project)
            : handleOpenLinkRepositoryModal(project),
        disabled: !isBitbucketConfigured,
        title: !isBitbucketConfigured
          ? "Please connect Bitbucket first to enable this functionality."
          : undefined,
      },
      // {
      //   label: 'Sync JIRA Items',
      //   icon: (
      //     <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      //       <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"></path>
      //     </svg>
      //   ),
      //   onClick: () => handleSyncJiraItems(project.id)
      // },
      {
        label: "Upload Requirements",
        icon: (
          <>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="17 8 12 3 7 8"></polyline>
              <line x1="12" y1="3" x2="12" y2="15"></line>
            </svg>
          </>
        ),
        title: "Upload Requirements",
        onClick: () => handleUploadRequirements(project),
      },
      {
        label: "Crawl Project",
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M19.07 4.93A10 10 0 0 0 6.99 3.34"></path>
            <path d="M4 6h.01"></path>
            <path d="M2.29 9.62A10 10 0 1 0 21.31 8.35"></path>
            <path d="M16.24 7.76A6 6 0 1 0 8.23 16.67"></path>
            <path d="M12 18h.01"></path>
            <path d="M17.99 11.66A6 6 0 0 1 15.77 16.67"></path>
            <circle cx="12" cy="12" r="2"></circle>
            <path d="m13.41 10.59 5.66-5.66"></path>
          </svg>
        ),
        title: "Crawl Project",
        onClick: () => handleCrawlProject(project),
      },
      {
        label: project?.originalData?.test_plan
          ? "Update Test Plan"
          : "Add Test Plan",
        icon: TesPlanIcon(project),
        onClick: () =>
          project?.originalData?.test_plan
            ? handleUpdateTestPlan(project.id)
            : handleAddTestPlan(project.id),
        disabled: true,    
        title: "Coming Soon",
      },

      {
        label: "Test Data Management",
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="3" width="7" height="7"></rect>
            <rect x="14" y="3" width="7" height="7"></rect>
            <rect x="14" y="14" width="7" height="7"></rect>
            <rect x="3" y="14" width="7" height="7"></rect>
          </svg>
        ),
        submenu: [
          {
            label: "Test Profile Data",
            icon: (
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
                <span
                  style={{
                    color: "red",
                    fontWeight: "bold",
                    cursor: "help",
                  }}
                  title="Coming Soon"
                >
                  *
                </span>
              </>
            ),
            disabled: true,
            title: "Coming Soon",
            onClick: () => navigateToTestProfileData(project.id),
          },
          {
            label: "Environment",
            icon: (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="12" x2="16" y2="12"></line>
              </svg>
            ),
            onClick: () => handleAddEnvironment(project.id),
          },
        ],
      },
      { type: "divider" },
      // {
      //   label: 'Epics and Stories',
      //   icon: (
      //     <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      //       <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
      //       <polyline points="14,2 14,8 20,8"></polyline>
      //       <line x1="16" y1="13" x2="8" y2="13"></line>
      //       <line x1="16" y1="17" x2="8" y2="17"></line>
      //       <polyline points="10,9 9,9 8,9"></polyline>
      //     </svg>
      //   ),
      //   onClick: () => handleNavigateToEpicsStories(project.id)
      // },
      {
        icon: (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>

            <span
              style={{
                color: "red",
                fontWeight: "bold",
                cursor: "help",
              }}
              title="Coming Soon"
            >
              *
            </span>
          </span>
        ),
        onClick: () => {},
        disabled: true,
        title: "Coming Soon",
        label: "Duplicate",
      },
      {
        label: project.status === "archived" ? "Unarchive" : "Archive",
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="1" y="3" width="22" height="5"></rect>
            <line x1="10" y1="12" x2="14" y2="12"></line>
          </svg>
        ),
        onClick: () =>
          project.status === "archived"
            ? handleUnarchiveProject(project.id)
            : handleArchiveProject(project.id),
      },
    ];
  };

  useEffect(() => {
    setFilteredProjects(Array.isArray(projects) ? projects : []);
  }, [projects]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };


  const handleDuplicateProject = (id) => {
    const project = projects.find(p => p.id === id);
    if (project) {
      setProjectToDuplicate(project);
      setDuplicateModalOpen(true);
    }
  };

  const handleDuplicateConfirm = async (newProjectName) => {
    if (!projectToDuplicate) return;
    await duplicateProject(projectToDuplicate.id, newProjectName);
    setDuplicateModalOpen(false);
    setProjectToDuplicate(null);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeDropdown !== null && !event.target.closest('.action-btn.more') && !event.target.closest('.action-dropdown')) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [activeDropdown]);



  const handleViewProject = (id) => {
    const project = projects.find(p => p.id === id);
    if (project) {
      setSelectedProject(project);
      setIsViewModalOpen(true);
    }
  };

  /** Enter project workspace (Overview / Test Cases / Suites / Plans). */
  const handleOpenProject = (id) => {
    const project = projects.find((p) => String(p.id) === String(id));
    if (!project) return;
    const workspaceId = getWorkspaceIdFromURL();
    selectProject(String(project.id), project.name);
    if (workspaceId) {
      navigate(buildWorkspaceDashboardPath(workspaceId, project.id));
    } else {
      navigate(`/dashboard`);
    }
    toast.success(`Opened "${project.name}"`);
  };
  // const handleAddTestProfileData = (id) => {
  //   if (id) {
  //      navigate(`/dashboard/project-test-data/${id}`);
  //   }
  // };
  const navigateToTestProfileData = (id) => {
    if (id) {
       navigate(`/dashboard/test-data-list/${id}`);
    }
  }

const handleAddEnvironment = (id) => {
    if (id) {
       navigate(`/dashboard/project-environment-list/${id}`);
    }
  };

const handleViewJiraItems = (id , integrationSource) => {
  const project = projects.find(p => p.id === id);
  if (project) {
     const urlFriendlyName = project.name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')  
      .replace(/^-+|-+$/g, ''); 
    
     navigate(`/dashboard/jira-items/${project.id}-${urlFriendlyName}`, {state: { integrationSource }});
  }
};

const handleAddTestPlan = (id) => {
  if (id) {
     navigate(`/dashboard/project-test-plan/${id}`);
  }
};


const handleUpdateTestPlan = (id) => {
  if (id) {
     navigate(`/dashboard/project-test-plan/${id}?mode=edit`);
  }
}

  const handleEditProject = (project) => {
    if (!canEditProject(project)) {
      const status = getProjectStatus(project) || 'inactive';
      showActionFeedback(
        `Project "${project.name}" is ${status} and cannot be edited`,
        'warning'
      );
      return;
    }

    setSelectedProject(project);
    setEditName(project.name);
    setEditDescription(project.description);
    setEditProjectType(project.type);
    setEditProjectStatus(project.status);
    setIsEditModalOpen(true);
  };

  const handleDeleteProject = async (id) => {
    const project = projects.find(p => p.id === id);
    if (!project || !canDeleteProject(project)) {
      if (project) {
        const status = getProjectStatus(project) || 'inactive';
        showActionFeedback(
          `Project "${project.name}" is ${status} and cannot be deleted`,
          'warning'
        );
      }
      return false;
    }

    try {
      const response = await axiosInstance.delete(`/projects/delete_project?project_id=${id}`);

      if (response.status_code === 200) {
        // Update local state to remove the deleted project
        // setFilteredProjects(prevProjects => prevProjects.filter(p => p.id !== id));
        
        showActionFeedback('Project deleted successfully', 'success');
        
        // Refresh the projects list to ensure consistency
        fetchProjects(currentPage, activeTab);
        
        setIsDeleteModalOpen(false);
        setProjectToDelete(null);
        return true;
      } 
      // else {
      //   throw new Error(response?.message || 'Failed to delete project');
      // }
    } catch (err) {
      console.error('Delete error:', err);
      showActionFeedback(`Failed to delete project: ${err.message}`, 'error');
      return false;
    }
  };

  const handleArchiveProject = async (id) => {
    await updateProjectStatus(id, 'archived');
  };

  const handleOpenLinkRepositoryModal = (project) => {
    setProjectForLinkRepository(project);
    setRepositoryLinkEditMode(false);
    setRepositoryLinkInitialData(null);
    setIsLinkRepositoryModalOpen(true);
  };

  const handleOpenLinkProjectModal = (project) => {
    setProjectForLinkProject(project);
    setIsLinkProjectModalOpen(true);
  };

  const handleLinkProjectSuccess = async () => {
    // Refresh the projects list
    await fetchProjects(currentPage, activeTab);
  };

  const handleOpenUpdateRepositoryModal = async (project) => {
    try {
      const response = await axiosInstance.get(`/get_project_repo?project_id=${project.id}`);
      if (response) {
        setProjectForLinkRepository(project);
        setRepositoryLinkInitialData(response);
        setRepositoryLinkEditMode(true);
        setIsLinkRepositoryModalOpen(true);
      } else {
        toast.error('Failed to fetch repository details', {
          position: 'top-right',
          duration: 3000,
        });
      }
    } catch (error) {
      console.error('Error fetching repository details:', error);
      toast.error('Failed to fetch repository details', {
        position: 'top-right',
        duration: 3000,
      });
    }
  };

  const handleRepositoryLinkSuccess = async () => {
    // Refresh the projects list with current filter state
    await fetchProjects(currentPage, activeTab);
  };

  const handleSyncJiraItems = (id) => {
    const project = projects.find(p => p.id === id);
    if (project) {
      setProjectForJiraSync(project);
      setIsJiraSyncModalOpen(true);
    }
  };

  const handleNavigateToEpicsStories = (projectId) => {
    navigate(`/dashboard/epics-stories-list?project_id=${projectId}`);
  };

  // Handle sorting
  const handleSort = (column) => {
    const sortTypeMap = {
      'project_name': 1,
      // 'progress': 2,
      // 'pass_rate': 3,
      'created': 2
    };

    let direction = 'asc';
    if (sortConfig.key === column && sortConfig.direction === 'asc') {
      direction = 'desc';
    }

    const newSortConfig = {
      key: column,
      direction: direction,
      sort_type: sortTypeMap[column],
      sort_order: direction === 'asc' ? 1 : 2
    };

    setSortConfig(newSortConfig);
  };

  // Render sort icon
  const renderSortIcon = (column) => {
    if (sortConfig.key !== column) {
      return (
        <svg className="w-4 h-4 ml-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }

    return sortConfig.direction === 'asc' ? (
      <svg className="w-4 h-4 ml-1 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 ml-1 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };  // showActionFeedback function moved up to be available for useProjectData hook

  const handleJiraSyncWithAccount = async (projectId, jiraAccount) => {
    try {
      const authDataString = localStorage.getItem('user');
      if (!authDataString) {
        throw new Error('User not authenticated - no auth data found');
      }

      const authData = JSON.parse(authDataString);
      const accessToken = authData.accessToken;
      if (!accessToken) {
        throw new Error('Access token not found in auth data');
      }

      const response = await axios.post(
        `${API_BASE_URL}/jira/sync_jira_items`,
        {
          project_id: projectId,
          jira_account_id: jiraAccount.id
        },
        {
          headers: {
            'token': `${accessToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        const project = projects.find(p => p.id === projectId);
        if (project) {
          showActionFeedback(`Successfully synced JIRA items for ${project.name} using ${jiraAccount.name}`, 'success');
        }
      } else {
        throw new Error(response.data.message || 'Failed to sync JIRA items');
      }
    } catch (err) {
      console.error('JIRA sync error:', err);
      showActionFeedback(`Failed to sync JIRA items: ${err.message}`, 'error');
    }
  };

  const handleJiraImport = async (jiraProjects) => {
    try {
      const authDataString = localStorage.getItem('user');
      if (!authDataString) {
        throw new Error('User not authenticated - no auth data found');
      }

      const authData = JSON.parse(authDataString);
      const accessToken = authData.accessToken;
      if (!accessToken) {
        throw new Error('Access token not found in auth data');
      }

      const response = await axios.get(
        `${API_BASE_URL}/jira_data/jira_data_projects`,
        {
          projects: jiraProjects
        },
        {
          headers: {
            'token': `${accessToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      // const response = await axiosInstance.get(
      //   `/jira_data/jira_data_projects`,
      //   {
      //     projects: jiraProjects
      //   },
      //   {
      //     headers: {
      //       'token': `${accessToken}`,
      //       'Content-Type': 'application/json'
      //     }
      //   }
      // );

      if (response.data.success) {
        const newProjects = jiraProjects.map((project, index) => ({
          ...project,
          id: Math.max(...projects.map(p => p.id), 0) + index + 1
        }));

        const updatedProjects = [...projects, ...newProjects];
        projects(updatedProjects);
        setFilteredProjects(updatedProjects);

        showActionFeedback(`Successfully imported ${newProjects.length} project(s) from JIRA`, 'success');
      } else {
        throw new Error(response.data.message || 'Failed to import JIRA projects');
      }
    } catch (err) {
      console.error('JIRA import error:', err);
      // showActionFeedback(`Failed to import JIRA projects: ${err.message}`, 'error');
    }
  };

  const handleJiraImportSuccess = async (jiraProjects) => {
    try {
      await fetchProjects();
      // showActionFeedback(`Successfully synced ${jiraProjects.length} project(s) from JIRA`, 'success');
    } catch (err) {
      console.error('Error refreshing project list after JIRA sync:', err);
      // showActionFeedback('Projects synced but failed to refresh list. Please refresh the page.', 'warning');
    }
  };

  const handleCrawlProject = (project) => {
    if (project?.id) {
      navigate(`/dashboard/project-crawl/${project.id}`);
    }
  };

  const handleUploadRequirements = (project) => {
    if (project?.id) {
      navigate(`/dashboard/project-requirement-upload/${project.id}`);
    }
  };

  const handleUpdateProject = async () => {
    if (!selectedProject) return;

    if (!canEditProject(selectedProject)) {
      const status = getProjectStatus(selectedProject) || 'inactive';
      showActionFeedback(
        `Project "${selectedProject.name}" is ${status} and cannot be edited`,
        'warning'
      );
      setIsEditModalOpen(false);
      return false;
    }

    try {
      const response = await axiosInstance.put(
        `/projects/update_project?project_id=${selectedProject.id}`,
        {
          project_name: editName,
          description: editDescription,
          project_type: editProjectType,
          status: editProjectStatus,
        }
      );

      if (response.status_code == 200) {
        // showActionFeedback('Project updated successfully!', 'success');
        setIsEditModalOpen(false);
        // Refresh the projects list
        fetchProjects(currentPage, activeTab);
        return true;
      } else {
        throw new Error(response?.message || 'Failed to update project');
      }
    } catch (err) {
      console.error('Update error:', err);
      showActionFeedback(`Failed to update project: ${err.message}`, 'error');
      return false;
    }
  };


  const safeProjects = Array.isArray(filteredProjects) ? filteredProjects : [];
  return (
    <>
      <div className={`projects-container projects-list-layout${embedded ? ' rt-projects-embedded' : ''}`}>
        <div className="projects-header">
          <div>
            {!embedded && (
              <>
                <h1 className="projects-title">Projects</h1>
                <p className="projects-subtitle">
                  Manage your test automation projects
                </p>
              </>
            )}
            <div className="search-containers relative max-w-md">
              <input
                type="text"
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="rt-projects-search"
                aria-label="Search projects"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg
                  className="h-5 w-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  type="button"
                  aria-label="Clear search"
                >
                  <svg
                    className="h-5 w-5 text-gray-400 hover:text-gray-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>
          <div className="projects-actions">
            <div className="button-group">
              <button
                type="button"
                className="rt-btn rt-btn--secondary"
                onClick={() => setIsJiraModalOpen(true)}
              >
                Import Projects
              </button>
              <button
                type="button"
                className="rt-btn rt-btn--primary"
                onClick={() => setCreateProjectModalOpen(true)}
              >
                + Create Project
              </button>
            </div>
          </div>
        </div>

        <div className="status-tabs-container">
          <div className="status-tabs">
            {/* <button
              className={`status-tab ${activeTab === 'all' ? 'active' : ''}`}
              onClick={() => handleTabChange('all')}
            >
              <span className="tab-label">All</span>
              <span className="tab-count">{statusCounts.all}</span>
            </button> */}
            <button
              className={`status-tab ${activeTab === "active" ? "active" : ""}`}
              onClick={() => handleTabChange("active")}
            >
              <span className="tab-label">Active</span>
              <span className="tab-count">{statusCounts.active}</span>
            </button>
            <button
              className={`status-tab ${activeTab === "inactive" ? "active" : ""}`}
              onClick={() => handleTabChange("inactive")}
            >
              <span className="tab-label">Inactive</span>
              <span className="tab-count">{statusCounts.inactive}</span>
            </button>
            <button
              className={`status-tab ${activeTab === "archived" ? "active" : ""}`}
              onClick={() => handleTabChange("archived")}
            >
              <span className="tab-label">Archived</span>
              <span className="tab-count">{statusCounts.archived}</span>
            </button>
          </div>
        </div>

        <div className="projects-table-container" ref={tableContainerRef}>
          <table className="projects-table">
            <thead>
              <tr>
                <th className="column-srno" style={{ textAlign: "center" }} title="Sr.No">Sr.No</th>
                <th
                  className="column-name sortable-header cursor-pointer hover:bg-gray-50"
                  onClick={() => handleSort("project_name")}
                >
                  <div className="flex items-center">
                    Project Name
                    {renderSortIcon("project_name")}
                  </div>
                </th>
                <th className="column-type" style={{ textAlign: "center" }} title="Type">Type</th>
                <th className="column-status" style={{ textAlign: "center" }} title="Status">Status</th>
                {/* <th 
                  className="sortable-header cursor-pointer hover:bg-gray-50"
                  onClick={() => handleSort('progress')}
                >
                  <div className="flex items-center">
                    Progress
                    {renderSortIcon('progress')} 
                  </div>
                </th> */}
                <th className="column-testcases" style={{ textAlign: "center" }} title="Test Cases">Test Cases</th>
                {/* <th 
                  className="sortable-header cursor-pointer hover:bg-gray-50"
                  onClick={() => handleSort('pass_rate')}
                >
                  <div className="flex items-center">
                    Pass Rate
                    {/* {renderSortIcon('pass_rate')} 
                  </div>
                </th> */}
                <th className="column-createdby" title="Created By">Created By</th>
                <th
                  className="column-created sortable-header cursor-pointer hover:bg-gray-50"
                  onClick={() => handleSort("created")}
                >
                  <div className="flex items-center">
                    Created
                    {renderSortIcon("created")}
                  </div>
                </th>
                <th className="column-updated" title="Last Updated">Last Updated</th>
                <th className="column-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {safeProjects.length > 0 ? (
                safeProjects.map((project, index) => {
                  const formattedCreatedAt = dateFormat(project.createdAt);
                  const formattedUpdatedAt = dateFormat(project.updatedAt);
                  const showEditButton =
                    canEditProject(project) &&
                    activeTab !== 'inactive' &&
                    activeTab !== 'archived';

                  return (
                  <tr key={project?.id || index}>
                    <td className="column-srno">
                      <span>{(currentPage - 1) * pageSize + index + 1}.</span>
                    </td>
                    <td
                      onClick={() => handleOpenProject(project.id)}
                      className="column-name cursor-pointer"
                      title="Open project"
                    >
                      <div className="cell-truncate project-name-text" title={project.name}>
                        {project.name}
                      </div>
                      {project.description && (
                        <div className="cell-truncate project-description-text" title={project.description}>
                          {project.description}
                        </div>
                      )}
                    </td>
                    <td className="column-type" style={{ overflow: "visible", textAlign: "center" }}>
                      <ProjectTypeBadge
                        type={project.type}
                        as="div"
                        style={{
                          width: "100%",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          overflow: "hidden",
                          display: "block",
                        }}
                      />
                    </td>
                    <td className="column-status" style={{ textAlign: "center" }}>
                      <ProjectStatusBadge
                        status={project.status}
                        as="div"
                        style={{
                          width: "100%",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          overflow: "hidden",
                          display: "block",
                        }}
                      />
                    </td>
                    {/* <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div style={{ width: '100px', height: '8px', backgroundColor: '#e9ecef', borderRadius: '4px', overflow: 'hidden' }}>
                          <div
                            style={{
                              height: '100%',
                              width: `${project.progress}%`,
                              backgroundColor:
                                project.progress < 30 ? '#dc3545' :
                                  project.progress < 70 ? '#fd7e14' :
                                    '#28a745',
                              borderRadius: '4px'
                            }}
                          ></div>
                        </div>
                        <span>{project.progress}%</span>
                      </div>
                    </td> */}
                    <td className="column-testcases">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <span className="test-cases-badge">
                          {project.testCases}
                        </span>
                      </div>
                    </td>
                    {/* <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div style={{ width: '60px', height: '8px', backgroundColor: '#e9ecef', borderRadius: '4px', overflow: 'hidden' }}>
                          <div
                            style={{
                              height: '100%',
                              width: `${project.passRate}%`,
                              backgroundColor:
                                project.passRate < 70 ? '#dc3545' :
                                  project.passRate < 90 ? '#fd7e14' :
                                    '#28a745',
                              borderRadius: '4px'
                            }}
                          ></div>
                        </div>
                        <span className={
                          project.passRate < 70 ? 'pass-rate-low' :
                            project.passRate < 90 ? 'pass-rate-medium' :
                              'pass-rate-high'
                        }>{project.passRate}%</span>
                      </div>
                    </td> */}
                    <td className="column-createdby">
                      <span className="cell-truncate" title={project.createdBy}>{project.createdBy}</span>
                    </td>
                    <td className="column-created">
                      <span className="cell-truncate" title={formattedCreatedAt}>{formattedCreatedAt}</span>
                    </td>
                    <td className="column-updated">
                      <span className="cell-truncate" title={formattedUpdatedAt}>{formattedUpdatedAt}</span>
                    </td>

                    <td className="column-actions">
                      <div className="action-buttons">
                        <button
                          className="action-btn view"
                          title="Open project"
                          onClick={() => handleOpenProject(project.id)}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                            <circle cx="12" cy="12" r="3"></circle>
                          </svg>
                        </button>
                        {showEditButton ? (
                          <button
                            className="action-btn edit"
                            title="Edit Project"
                            onClick={() => handleEditProject(project)}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                            </svg>
                          </button>
                        ) : null}
                        {canDeleteProject(project) ? (
                          <button
                            className="action-btn delete"
                            title="Delete Project"
                            onClick={() => {
                              setProjectToDelete(project);
                              setIsDeleteModalOpen(true);
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="3 6 5 6 21 6"></polyline>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            </svg>
                          </button>
                        ) : null}
                        <div className="action-btn-container">
                          <button
                            className="action-btn more"
                            title="More Actions"
                            onClick={(e) => handleMoreClick(e, project.id)}
                            ref={dropdownTriggerRef}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <circle cx="12" cy="12" r="1"></circle>
                              <circle cx="12" cy="5" r="1"></circle>
                              <circle cx="12" cy="19" r="1"></circle>
                            </svg>
                          </button>
                          <DropdownMenu
                            isOpen={activeDropdown === project.id}
                            onClose={() => setActiveDropdown(null)}
                            items={getDropdownItems(project)}
                            triggerRef={dropdownTriggerRef}
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="9" className="no-projects-message">
                    {
                      /* {activeTab === 'all'
                      ? "There are no projects"
                      :*/ activeTab === "active" && statusCounts.active === 0
                        ? "There are no active projects"
                        : activeTab === "inactive" &&
                            statusCounts.inactive === 0
                          ? "There are no inactive projects"
                          : activeTab === "archived" &&
                              statusCounts.archived === 0
                            ? "There are no archived projects"
                            : `There are no ${activeTab} projects`
                    }
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

          {filteredProjects.length > 0 && (
        <div className="pagination-container">
          <Pagination
            page={currentPage}
            pageSize={pageSize}
            totalPages={totalPages}
            total={Math.max(
              filteredProjects.length,
              (Math.max(totalPages, 1) - 1) * pageSize + filteredProjects.length
            )}
            onPageChange={(page) => {
              setCurrentPage(page);
              tableContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
            }}
            showPageSize={false}
          />
        </div>
      )}
      </div>

      {/* <>
          <div className="projects-header">
            <div>
              <h1 className="projects-title">Projects</h1>
              <p className="projects-subtitle">Manage your test automation projects</p>
            </div>
            <div className="projects-actions">
              <div className="button-group">
                <button
                  className="navbar-button secondary import-jira-btn"
                  onClick={() => setIsJiraModalOpen(true)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="17 8 12 3 7 8"></polyline>
                    <line x1="12" y1="3" x2="12" y2="15"></line>
                  </svg>
                  Import from JIRA
                </button>
                <button
                  className="navbar-button primary create-project-btn"
                  onClick={() => setCreateProjectModalOpen(true)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="create-project-icon">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                  </svg>
                  Create Project
                </button>
              </div>
            </div>
          </div>
          <div className="empty-state">
            <div className="empty-state-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="9" y1="9" x2="15" y2="15"></line>
                <line x1="15" y1="9" x2="9" y2="15"></line>
              </svg>
            </div>
            <h2 className="empty-state-title">No projects found</h2>
            <p className="empty-state-description">
              There are no {activeTab} projects matching your search criteria.
            </p>
            <button
              className="navbar-button primary create-project-btn"
              onClick={onCreateProject}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="create-project-icon">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              Create Project
            </button>
          </div>
        </> */}

      {/* View Project Modal */}
      {isViewModalOpen && selectedProject && (
        <ViewModal
          selectedProject={selectedProject}
          onClose={() => setIsViewModalOpen(false)}
        />
      )}

      {/* Edit Project Modal */}
      {isEditModalOpen && selectedProject && (
        <EditModal
          selectedProject={selectedProject}
          setIsEditModalOpen={setIsEditModalOpen}
          editName={editName}
          setEditName={setEditName}
          editDescription={editDescription}
          setEditDescription={setEditDescription}
          editProjectType={editProjectType}
          setEditProjectType={setEditProjectType}
          editProjectStatus={editProjectStatus}
          setEditProjectStatus={setEditProjectStatus}
          handleUpdateProject={handleUpdateProject}
          isLoading={isLoading}
        />
      )}

      {/* Delete Project Modal */}
      {isDeleteModalOpen && projectToDelete && (
        <DeleteModal
          handleDeleteProject={handleDeleteProject}
          projectToDelete={projectToDelete}
          setIsDeleteModalOpen={setIsDeleteModalOpen}
          setProjectToDelete={setProjectToDelete}
        />
      )}

      {/* Create Project Modal */}
      {createProjectModalOpen && (
        <CreateProjectModal
          toggleCreateProjectModal={() => setCreateProjectModalOpen(false)}
          projectName={projectName}
          setProjectName={setProjectName}
          projectDescription={projectDescription}
          setProjectDescription={setProjectDescription}
          projectType={projectType}
          setProjectType={setProjectType}
          handleCreateProject={handleCreateProject}
          loading={loading}
        />
      )}

      {/* JIRA Import Modal */}
      <JiraImportModal
        isOpen={isJiraModalOpen}
        onClose={() => setIsJiraModalOpen(false)}
        onImport={handleJiraImportSuccess}
      />

      {/* Link Project Modal */}
      <LinkProjectModal
        isOpen={isLinkProjectModalOpen}
        onClose={() => {
          setIsLinkProjectModalOpen(false);
          setProjectForLinkProject(null);
        }}
        projectId={projectForLinkProject?.id}
        onLinkSuccess={handleLinkProjectSuccess}
      />

      {/* Link Repository Modal */}
      <LinkRepositoryModal
        isOpen={isLinkRepositoryModalOpen}
        onClose={() => {
          setIsLinkRepositoryModalOpen(false);
          setProjectForLinkRepository(null);
          setRepositoryLinkEditMode(false);
          setRepositoryLinkInitialData(null);
        }}
        onSuccess={handleRepositoryLinkSuccess}
        projectId={projectForLinkRepository?.id}
        isEditMode={repositoryLinkEditMode}
        initialData={repositoryLinkInitialData}
      />

      {/* <Navbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        fetchProjects={fetchProjects}
      /> */}

      {/* JIRA Sync Modal */}
      <JiraSyncModal
        isOpen={isJiraSyncModalOpen}
        onClose={() => setIsJiraSyncModalOpen(false)}
        onSync={handleJiraSyncWithAccount}
        project={projectForJiraSync}
      />

      <DuplicateModal
        isOpen={duplicateModalOpen}
        onClose={() => {
          setDuplicateModalOpen(false);
          setProjectToDuplicate(null);
        }}
        project={projectToDuplicate}
        onDuplicate={handleDuplicateConfirm}
      />

      {/* Action Feedback */}
      {actionFeedback && (
        <div className={`action-feedback ${actionFeedback.type}`}>
          <div className="action-feedback-content">
            <span className="action-feedback-message">
              {actionFeedback.message}
            </span>
            <button
              className="action-feedback-close"
              onClick={() => setActionFeedback(null)}
            >
              ×
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ProjectsList;